import { sort } from 'd3-array';
import {
  addCommasToNumbers,
  toTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import {
  RiskCompExposureChangesResponse,
  RiskCompPositionChange,
  RiskCompVarBreakdownResponse,
} from '../risk-comparison.types';
import { ComparisonSection } from '../RiskComparisonTable';
import { IRiskComparisonTableExposureChanges } from './exposureChangesTable.data';
import { IRiskComparisonTablePositionChangesData } from './positionChangesTable.data';
import { IRiskComparisonTableVarChanges } from './varChangesTable.data';

type IRiskComparisonDataType =
  | IRiskComparisonTableExposureChanges
  | IRiskComparisonTablePositionChangesData
  | IRiskComparisonTableVarChanges;

export function buildComparisonData(
  selectedSection: ComparisonSection,
  changeData:
    | RiskCompExposureChangesResponse
    | RiskCompPositionChange[]
    | RiskCompVarBreakdownResponse
    | null
    | undefined,
  referenceDate: string,
  comparisonDate: string,
): IRiskComparisonDataType[] {
  if (!changeData) return [];

  const returnData: IRiskComparisonDataType[] = [];

  switch (selectedSection) {
    case 'exposureChanges': {
      const data = changeData as RiskCompExposureChangesResponse;
      data.forEach((row, index) => {
        returnData.push({
          number: index + 1,
          name: row[1],
          class: row[2],
          type: row[3],
          currency: row[4],
          localCurrencyExposure_pc_ref: row[5],
          baseCurrencyExposure_pc_ref: row[6],
          grossCurrencyExposure_pc_ref: row[7],
          localCurrencyExposure_pc_comp: row[8],
          baseCurrencyExposure_pc_comp: row[9],
          grossCurrencyExposure_pc_comp: row[10],
          localCurrencyExposure_pc_diff: row[11],
          baseCurrencyExposure_pc_diff: row[12],
          grossCurrencyExposure_pc_diff: row[13],
        });
      });

      returnData.unshift({
        number: '',
        name: '',
        class: '',
        type: '',
        currency: '',
        localCurrencyExposure_pc_ref: '',
        baseCurrencyExposure_pc_ref: referenceDate,
        grossCurrencyExposure_pc_ref: '',
        localCurrencyExposure_pc_comp: '',
        baseCurrencyExposure_pc_comp: comparisonDate,
        grossCurrencyExposure_pc_comp: '',
        localCurrencyExposure_pc_diff: '',
        baseCurrencyExposure_pc_diff: 'Difference',
        grossCurrencyExposure_pc_diff: '',
      });
      break;
    }

    case 'varChanges': {
      // const data = changeData as RiskCompVarBreakdownResponse;

      const data = changeData as any;

      // Weird legacy logic, needs explanation
      data.ne.forEach((row: any, index: any) => {
        returnData.push({
          number: index > 0 ? index : '',
          netExposureRef_Title: row[0],
          netExposureRef: row[1],
          netExposureComp_Title: row[2],
          netExposureComp: row[3],
          varRef: '',
          varRefTitle: '',
          varCompTitle: '',
          varComp: '',
          cvarRefTitle: '',
          cvarRef: '',
          cvarCompTitle: '',
          cvarComp: '',
          dvarRefTitle: '',
          dvarRef: '',
          dvarCompTitle: '',
          dvarComp: '',
        });
      });

      data.var.forEach((row: any, index: any) => {
        returnData[index] = {
          ...returnData[index],
          varRefTitle: row[0],
          varRef: row[1],
          varCompTitle: row[2],
          varComp: row[3],
        };
      });

      data.cvar.forEach((row: any, index: any) => {
        returnData[index + 1] = {
          ...returnData[index + 1],
          cvarRefTitle: row[0],
          cvarRef: row[1],
          cvarCompTitle: row[2],
          cvarComp: row[3],
        };
      });

      data.dvar.forEach((row: any, index: any) => {
        returnData[index + 1] = {
          ...returnData[index + 1],
          dvarRefTitle: row[0],
          dvarRef: row[1],
          dvarCompTitle: row[2],
          dvarComp: row[3],
        };
      });

      returnData.unshift({
        number: 'Date',
        netExposureRef_Title: referenceDate,
        netExposureRef: '',
        netExposureComp_Title: comparisonDate,
        netExposureComp: '',
        varRefTitle: referenceDate,
        varRef: '',
        varCompTitle: comparisonDate,
        varComp: '',
        cvarRefTitle: referenceDate,
        cvarRef: '',
        cvarCompTitle: comparisonDate,
        cvarComp: '',
        dvarRefTitle: referenceDate,
        dvarRef: '',
        dvarCompTitle: comparisonDate,
        dvarComp: '',
      });

      break;
    }

    case 'positionChanges': {
      const data = changeData as RiskCompPositionChange[];

      data.forEach((row, index) => {
        const ref = Number(row.reference_mr_position) || 0;
        const comp = Number(row.mr_position) || 0;

        const tradePercent = ref !== 0 ? ((comp - ref) / ref) * 100 : 0;

        returnData.push({
          number: index + 1,
          genericMarket: row.gm_name,
          instrument: row.instrument_type || 'Unspecified',
          info: row.expiry_date,
          positionReference: addCommasToNumbers(ref),
          comparisonPosition: addCommasToNumbers(comp),
          trade: `${tradePercent.toFixed(2)}%`,
          market: toTwoDecimalPlaces(row.used_underlying_price),
          risk: Math.round(row.risk_metric_value),
          portfolio: 'N/A',
          status: row.status,
          positionName: row.gm_name_long,
        });
      });
      returnData.sort(sortByStatusOrder as any); // Lazy to properly type

      break;
    }

    default:
      return [];
  }

  return returnData;
}

export default buildComparisonData;

export type RiskCompStatus =
  | 'altered position'
  | 'new position'
  | 'removed position'
  | 'no change';

export function sortByStatusOrder(
  a: { status: string },
  b: { status: string },
): number {
  const statusOrder: Record<RiskCompStatus, number> = {
    'altered position': 0,
    'new position': 1,
    'removed position': 2,
    'no change': 3,
  };

  return (
    (statusOrder[a.status as RiskCompStatus] ?? 99) -
    (statusOrder[b.status as RiskCompStatus] ?? 99)
  );
}
